import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react'
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { FiPlus, FiSliders, FiTrash2 } from 'react-icons/fi'

import { useAppToast } from '../../../helpers'
import ContestEvaluationCriteriaService from '../../../services/Contests/ContestEvaluationCriteriaService'
import { IContestEvaluationCriterion } from '../../../services/Contests/interfaces'
import { DEFAULT_EVALUATION_CRITERIA } from './evaluationCriteria'

interface ICriteriaManagementModalProps {
  isOpen: boolean
  onClose: () => void
  contestId: number | string
  contestName: string
  initialCriteria: IContestEvaluationCriterion[]
  required: boolean
  onSaved: (criteria: IContestEvaluationCriterion[]) => void
}

interface IEditableRow {
  key: string
  id?: number
  name: string
  weight: string
  nameError?: string
  weightError?: string
}

function randomKey(): string {
  return `new-${Math.random().toString(36).slice(2, 9)}`
}

function rowFromCriterion(criterion: IContestEvaluationCriterion): IEditableRow {
  return {
    key: `id-${criterion.id}`,
    id: criterion.id,
    name: criterion.name,
    weight: String(criterion.weight)
  }
}

function rowFromDefault(name: string, weight: number): IEditableRow {
  return { key: randomKey(), name, weight: String(weight) }
}

function parseWeight(value: string): number {
  const parsed = parseFloat(value.replace(',', '.'))
  return isNaN(parsed) ? 0 : parsed
}

export default function CriteriaManagementModal({
  isOpen,
  onClose,
  contestId,
  contestName,
  initialCriteria,
  required,
  onSaved
}: ICriteriaManagementModalProps) {
  const service = useMemo(() => new ContestEvaluationCriteriaService(), [])
  const toast = useAppToast()

  const [rows, setRows] = useState<IEditableRow[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const focusTargetRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    if (initialCriteria.length === 0) {
      setRows(DEFAULT_EVALUATION_CRITERIA.map((c) => rowFromDefault(c.name, c.weight)))
    } else {
      setRows(initialCriteria.map((c) => rowFromCriterion(c)))
    }
  }, [isOpen, initialCriteria])

  useEffect(() => {
    if (!focusTargetRef.current) return
    const target = inputRefs.current[focusTargetRef.current]
    if (target) {
      target.focus()
      target.select()
    }
    focusTargetRef.current = null
  }, [rows])

  const weightSum = useMemo(() => rows.reduce((acc, r) => acc + parseWeight(r.weight), 0), [rows])
  const sumIsValid = Math.abs(weightSum - 100) <= 0.01

  const handleAdd = () => {
    const newRow: IEditableRow = { key: randomKey(), name: '', weight: '0' }
    focusTargetRef.current = newRow.key
    setRows((prev) => [...prev, newRow])
  }

  const handleRemove = (key: string) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key)
      const next = prev.filter((r) => r.key !== key)
      const fallback = next[idx] || next[idx - 1]
      if (fallback) focusTargetRef.current = fallback.key
      delete inputRefs.current[key]
      return next
    })
  }

  const handleNameChange = (key: string, name: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, name, nameError: undefined } : r)))
  }

  const handleWeightChange = (key: string, weight: string) => {
    // Allow digits, a single separator and nothing else.
    if (weight !== '' && !/^\d*[.,]?\d*$/.test(weight)) return
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, weight, weightError: undefined } : r)))
  }

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>, key: string) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      const idx = rows.findIndex((r) => r.key === key)
      if (idx === rows.length - 1) {
        handleAdd()
      } else {
        focusTargetRef.current = rows[idx + 1].key
        setRows((prev) => [...prev])
      }
    }
  }

  const validate = (): boolean => {
    let valid = rows.length > 0
    const nextRows = rows.map((r) => {
      const next = { ...r, nameError: undefined as string | undefined, weightError: undefined as string | undefined }
      if (!r.name.trim()) {
        valid = false
        next.nameError = 'Nome obrigatório'
      }
      if (parseWeight(r.weight) <= 0) {
        valid = false
        next.weightError = 'Peso > 0'
      }
      return next
    })
    setRows(nextRows)

    if (!sumIsValid) valid = false

    return valid
  }

  const handleSave = async () => {
    if (!validate()) {
      if (!sumIsValid) {
        toast.warning('A soma dos pesos deve ser 100%.', `Soma atual: ${Number(weightSum.toFixed(2))}%.`)
      }
      return
    }

    setIsSaving(true)
    try {
      const result = await service.sync(contestId, {
        criteria: rows.map((r) => ({ id: r.id, name: r.name.trim(), weight: parseWeight(r.weight) }))
      })
      toast.success('Critérios salvos com sucesso')
      onSaved(result.data)
      onClose()
    } catch {
      toast.error('Erro ao salvar critérios')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (required && initialCriteria.length === 0) return
    onClose()
  }

  const canClose = !required || initialCriteria.length > 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnEsc={canClose}
      closeOnOverlayClick={canClose}
      isCentered
    >
      <ModalOverlay bg="rgba(17,24,39,0.6)" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="16px"
        overflow="hidden"
        bg="white"
        fontFamily="Inter, sans-serif"
        boxShadow="0 24px 60px -12px rgba(17,24,39,0.25)"
      >
        {canClose ? (
          <ModalCloseButton
            zIndex="2"
            bg="rgba(255,255,255,0.9)"
            borderRadius="full"
            size="sm"
            top="12px"
            right="12px"
            _hover={{ bg: 'white' }}
          />
        ) : null}

        <ModalHeader px="6" pt="6" pb="2">
          <Flex align="center" gap="3" mb="1">
            <Flex
              align="center"
              justify="center"
              w="40px"
              h="40px"
              borderRadius="10px"
              bg="magenta.50"
              color="magenta.500"
              fontSize="18px"
            >
              <FiSliders />
            </Flex>
            <Box>
              <Text fontSize="18px" fontWeight="700" color="gray.900" letterSpacing="-0.01em">
                Critérios de avaliação
              </Text>
              <Text fontSize="12px" color="gray.500" fontWeight="400">
                {contestName}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>

        <ModalBody px="6" pb="2">
          {required && initialCriteria.length === 0 && (
            <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="10px" p="3" mb="4">
              <Text fontSize="13px" color="orange.800" fontWeight="500">
                Cadastre os critérios de avaliação deste concurso antes de começar a avaliar as obras. Os pesos devem
                somar 100%.
              </Text>
            </Box>
          )}

          <Flex
            fontSize="11px"
            fontWeight="600"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="0.04em"
            mb="1.5"
            px="0.5"
          >
            <Box flex="1">Critério</Box>
            <Box w="110px" textAlign="center">
              Peso
            </Box>
            <Box w="42px" />
          </Flex>

          <VStack spacing="2.5" align="stretch">
            {rows.map((row, index) => (
              <Flex key={row.key} align="flex-start" gap="2">
                <Box flex="1">
                  <Input
                    ref={(el) => {
                      inputRefs.current[row.key] = el
                    }}
                    value={row.name}
                    onChange={(e) => handleNameChange(row.key, e.target.value)}
                    onKeyDown={(e) => handleNameKeyDown(e, row.key)}
                    placeholder={`Critério ${index + 1}`}
                    borderRadius="10px"
                    borderColor={row.nameError ? 'red.400' : 'gray.200'}
                    fontSize="14px"
                    h="42px"
                    bg="white"
                    autoComplete="off"
                    _placeholder={{ color: 'gray.400' }}
                    _hover={{ borderColor: row.nameError ? 'red.500' : 'gray.300' }}
                    _focus={{
                      borderColor: row.nameError ? 'red.500' : 'magenta.500',
                      boxShadow: row.nameError ? '0 0 0 3px rgba(239,68,68,0.12)' : '0 0 0 3px rgba(250,0,105,0.12)'
                    }}
                  />
                  {row.nameError && (
                    <Text fontSize="11px" color="red.500" mt="1">
                      {row.nameError}
                    </Text>
                  )}
                </Box>
                <Box w="110px">
                  <InputGroup>
                    <Input
                      value={row.weight}
                      onChange={(e) => handleWeightChange(row.key, e.target.value)}
                      inputMode="decimal"
                      textAlign="center"
                      borderRadius="10px"
                      borderColor={row.weightError ? 'red.400' : 'gray.200'}
                      fontSize="14px"
                      fontWeight="600"
                      h="42px"
                      bg="white"
                      pr="26px"
                      autoComplete="off"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                      _hover={{ borderColor: row.weightError ? 'red.500' : 'gray.300' }}
                      _focus={{
                        borderColor: row.weightError ? 'red.500' : 'magenta.500',
                        boxShadow: row.weightError ? '0 0 0 3px rgba(239,68,68,0.12)' : '0 0 0 3px rgba(250,0,105,0.12)'
                      }}
                    />
                    <InputRightElement h="42px" w="22px" color="gray.400" fontSize="13px" pointerEvents="none">
                      %
                    </InputRightElement>
                  </InputGroup>
                  {row.weightError && (
                    <Text fontSize="11px" color="red.500" mt="1" textAlign="center">
                      {row.weightError}
                    </Text>
                  )}
                </Box>
                <IconButton
                  aria-label="Remover critério"
                  icon={<FiTrash2 />}
                  variant="ghost"
                  color="gray.500"
                  borderRadius="10px"
                  h="42px"
                  w="42px"
                  isDisabled={rows.length === 1}
                  onClick={() => handleRemove(row.key)}
                  _hover={{ bg: 'red.50', color: 'red.500' }}
                />
              </Flex>
            ))}
          </VStack>

          <Flex justify="space-between" align="center" mt="3">
            <Button
              onClick={handleAdd}
              leftIcon={<FiPlus />}
              variant="ghost"
              color="magenta.500"
              fontSize="13px"
              fontWeight="600"
              borderRadius="10px"
              _hover={{ bg: 'magenta.50' }}
            >
              Adicionar critério
            </Button>

            <Flex
              align="center"
              gap="1.5"
              px="3"
              py="1.5"
              borderRadius="full"
              bg={sumIsValid ? 'green.50' : 'orange.50'}
              color={sumIsValid ? 'green.600' : 'orange.600'}
              fontSize="12px"
              fontWeight="600"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              <Box w="6px" h="6px" borderRadius="full" bg={sumIsValid ? 'green.400' : 'orange.400'} />
              Soma: {Number(weightSum.toFixed(2))}%{sumIsValid ? '' : ' (deve ser 100%)'}
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200" gap="2" px="6" py="4">
          {canClose && (
            <Button
              variant="ghost"
              onClick={onClose}
              fontSize="14px"
              fontWeight="600"
              color="gray.600"
              borderRadius="10px"
              _hover={{ bg: 'gray.100' }}
            >
              Cancelar
            </Button>
          )}
          <Button
            bg="magenta.500"
            color="white"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Salvando..."
            isDisabled={rows.length === 0}
            fontSize="14px"
            fontWeight="600"
            borderRadius="10px"
            px="6"
            _hover={{ bg: 'magenta.600' }}
            _active={{ transform: 'scale(0.98)' }}
            sx={{ transition: 'all .15s ease' }}
          >
            Salvar critérios
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
